import React, { PropsWithChildren, useState } from "react"
import { Transition } from "@headlessui/react"
import { Icon } from "~/src/components"
import { twMerge } from "tailwind-merge"

/**
 * Simple multi-slide carousel. Takes children that represent the content to show each slide. With 0 or 1 child, no carousel ui is displayed. With two or more children, a dot progress bar shows the current child, and clicking the carousel advances it.
 *
 * @param props.children The children to display per-slide in the carousel
 */
export function Carousel({ className, children }: PropsWithChildren<{ className: string }>) {
  const childList = React.Children.toArray(children)
  if (childList.length <= 1) {
    return <div className={className}>{children}</div>
  }

  const [currentIndex, setCurrentIndex] = useState(0)
  const [lhsIndex, setLhsIndex] = useState(0)
  const [rhsIndex, setRhsIndex] = useState(1)
  const [isSliding, setIsSliding] = useState(false)

  const nextIndex = currentIndex >= childList.length - 1 ? 0 : currentIndex + 1
  const startTransition = () => {
    if (!isSliding) {
      setIsSliding(true)
      setCurrentIndex(nextIndex)
    }
  }
  const endLhsTransition = () => {
    if (isSliding) {
      setIsSliding(false)
      setLhsIndex(nextIndex)
    }
  }

  const endRhsTransition = () => {
    if (isSliding) {
      setIsSliding(false)
      setRhsIndex(nextIndex)
    }
  }

  return (
    <div className={twMerge(className, "cursor-pointer")} onClick={startTransition}>
      <div className="flex flex-col w-full h-full">
        <div className="basis-5/6 shrink-0 relative overflow-x-hidden">
          <div className="absolute left-0 top-0">
            <Transition
              show={currentIndex == lhsIndex}
              enter="transition duration-500"
              leave="transition duration-500"
              enterFrom="translate-x-full opacity-50"
              leaveTo="-translate-x-full opacity-0"
              afterLeave={endLhsTransition}
            >
              <div>{childList[lhsIndex]}</div>
            </Transition>
          </div>

          <div className="absolute left-0 top-0">
            <Transition
              show={currentIndex == rhsIndex}
              enter="transition duration-500"
              leave="transition duration-500"
              enterFrom="translate-x-full opacity-50"
              leaveTo="-translate-x-full opacity-0"
              afterLeave={endRhsTransition}
            >
              <div>{childList[rhsIndex]}</div>
            </Transition>
          </div>
        </div>

        <div className="basis-1/6 flex justify-center items-end w-full gap-2 select-none">
          {childList.map((_, index) => {
            return <Icon.Dot key={index} className={index === currentIndex ? "text-navy-800" : "text-gray-200"} />
          })}
        </div>
      </div>
    </div>
  )
}
